<template>
    <div>
        <Modal v-if="pluginSubscription" size="xl" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">
                    <span v-if="selected !== 'cancel'">
                        Change subscription
                    </span>
                    <span v-else class="text-topol-alert-100">
                        Cancel subscription
                    </span>
                </div>
            </template>

            <template #body>
                <div class="px-6 pb-6 pt-3">
                    <div
                        class="flex flex-col rounded-[4px] px-[14px] pb-2.5 pt-3 sm:flex-row"
                        :class="{
                            'bg-blue-100': selected !== 'cancel',
                            'bg-red-100': selected === 'cancel',
                        }"
                    >
                        <div
                            :class="{
                                'text-blue-600': selected !== 'cancel',
                                'text-topol-alert-100': selected === 'cancel',
                            }"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M18 10C18 12.1217 17.1571 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18C7.87827 18 5.84344 17.1571 4.34315 15.6569C2.84285 14.1566 2 12.1217 2 10C2 7.87827 2.84285 5.84344 4.34315 4.34315C5.84344 2.84285 7.87827 2 10 2C12.1217 2 14.1566 2.84285 15.6569 4.34315C17.1571 5.84344 18 7.87827 18 10V10ZM11 6C11 6.26522 10.8946 6.51957 10.7071 6.70711C10.5196 6.89464 10.2652 7 10 7C9.73478 7 9.48043 6.89464 9.29289 6.70711C9.10536 6.51957 9 6.26522 9 6C9 5.73478 9.10536 5.48043 9.29289 5.29289C9.48043 5.10536 9.73478 5 10 5C10.2652 5 10.5196 5.10536 10.7071 5.29289C10.8946 5.48043 11 5.73478 11 6ZM9 9C8.73478 9 8.48043 9.10536 8.29289 9.29289C8.10536 9.48043 8 9.73478 8 10C8 10.2652 8.10536 10.5196 8.29289 10.7071C8.48043 10.8946 8.73478 11 9 11V14C9 14.2652 9.10536 14.5196 9.29289 14.7071C9.48043 14.8946 9.73478 15 10 15H11C11.2652 15 11.5196 14.8946 11.7071 14.7071C11.8946 14.5196 12 14.2652 12 14C12 13.7348 11.8946 13.4804 11.7071 13.2929C11.5196 13.1054 11.2652 13 11 13V10C11 9.73478 10.8946 9.48043 10.7071 9.29289C10.5196 9.10536 10.2652 9 10 9H9Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </div>
                        <div
                            class="mt-2 text-sm sm:ml-[14px] sm:mt-0"
                            :class="{
                                'text-blue-600': selected !== 'cancel',
                                'text-red-600': selected === 'cancel',
                            }"
                        >
                            You are currently using
                            <span
                                class="font-medium"
                                :class="{
                                    'text-blue-700': selected !== 'cancel',
                                    'text-red-700': selected === 'cancel',
                                }"
                                >{{ pluginSubscription.plan?.name }}</span
                            >
                            <span v-if="!hasPluginSubscriptionUnlimited">
                                for up to
                                {{ pluginSubscription.plan?.users_limit }}
                                users.
                            </span>

                            <span v-else> for unlimited users. </span>
                            <span
                                v-if="
                                    !pluginSubscription.canceled_at &&
                                    pluginSubscription.next_billing_date
                                "
                            >
                                Your plan

                                <span
                                    v-if="
                                        new Date(
                                            pluginSubscription.next_billing_date
                                        ) > new Date()
                                    "
                                    >will renew on</span
                                >
                                <span v-else>was about to renew on</span>
                                {{
                                    format(
                                        new Date(
                                            pluginSubscription.next_billing_date
                                        ),
                                        'd MMMM, yyyy'
                                    )
                                }}.
                            </span>
                        </div>
                    </div>

                    <div
                        class="mt-5 rounded-[6px] border border-gray-200 bg-gray-50 px-6 pb-6 pt-4"
                    >
                        <SelectInput
                            ref="selectInput"
                            v-model="selected"
                            :data="pluginPlans()"
                            label="Choose your subscription"
                            placeholder="Choose subscription"
                            class="max-w-[400px]"
                            @update:modelValue="getPreparedSubscription"
                        />

                        <div
                            v-if="pluginSubscription.plan?.uuid === selected"
                            class="mt-4"
                        >
                            <div
                                class="text-2xl leading-normal tracking-tighter text-gray-800"
                            >
                                <span
                                    v-if="
                                        pluginSubscription.amount_with_tax !==
                                        undefined
                                    "
                                    class="font-bold"
                                >
                                    ${{
                                        (
                                            pluginSubscription.amount_with_tax /
                                            100
                                        ).toFixed(2)
                                    }}
                                </span>

                                <span
                                    >/{{
                                        pluginSubscription.period === 1
                                            ? 'month'
                                            : 'year'
                                    }}
                                </span>
                            </div>
                            <div
                                v-if="pluginSubscription.plan"
                                class="max-w-[419px] text-sm text-gray-500"
                            >
                                {{ pluginSubscription.plan.description }}

                                <span v-if="!hasPluginSubscriptionUnlimited">
                                    {{ pluginSubscription.plan.users_limit }}
                                    users included in plan, additional user for
                                    ${{
                                        pluginSubscription.plan.users_price
                                            ? pluginSubscription.plan
                                                  .users_price / 100
                                            : 0
                                    }}/month. Paid monthly.
                                </span>
                            </div>

                            <div
                                v-if="
                                    selected ===
                                        pluginSubscription.plan?.uuid &&
                                    !pluginSubscription.canceled_at &&
                                    pluginSubscription.next_billing_date
                                "
                                class="mt-[18px] text-sm"
                            >
                                <div class="text-gray-500">
                                    {{
                                        pluginSubscription.period === 1
                                            ? 'Month-to-month'
                                            : 'Annual'
                                    }}
                                    plan
                                    <span
                                        v-if="
                                            new Date(
                                                pluginSubscription.next_billing_date
                                            ) > new Date()
                                        "
                                    >
                                        renews on
                                    </span>
                                    <span v-else
                                        >was scheduled to renew on</span
                                    >
                                </div>

                                <div class="font-medium text-gray-800">
                                    {{
                                        format(
                                            new Date(
                                                pluginSubscription.next_billing_date
                                            ),
                                            'd MMMM, yyyy'
                                        )
                                    }}
                                </div>
                            </div>
                            <div
                                v-if="pluginSubscription.canceled_at"
                                class="mt-[18px] text-sm text-gray-500"
                            >
                                <div>
                                    Subscription canceled, will be active until
                                </div>
                                <div class="font-medium text-gray-800">
                                    {{
                                        format(
                                            new Date(
                                                pluginSubscription.canceled_at
                                            ),
                                            'd MMMM, yyyy'
                                        )
                                    }}
                                </div>
                            </div>
                        </div>

                        <div v-if="pluginSubscription.plan?.uuid !== selected">
                            <div
                                v-if="
                                    selected !== 'cancel' &&
                                    preparedSubscription
                                "
                            >
                                <div
                                    class="mt-4 text-2xl leading-normal tracking-tighter text-gray-800"
                                >
                                    <span class="font-bold">
                                        ${{
                                            (
                                                preparedSubscription.total / 100
                                            ).toFixed(2)
                                        }}
                                    </span>

                                    <span
                                        >/{{
                                            preparedSubscription
                                                .subscription_plan.period === 1
                                                ? 'month'
                                                : 'year'
                                        }}
                                    </span>
                                </div>

                                <div
                                    class="max-w-[419px] text-sm text-gray-500"
                                >
                                    {{
                                        preparedSubscription.subscription_plan
                                            .description
                                    }}
                                    <span
                                        v-if="
                                            !hasPreparedPluginSubscriptionUnlimited
                                        "
                                    >
                                        {{
                                            preparedSubscription
                                                .subscription_plan.users_limit
                                        }}
                                        users included in plan, additional user
                                        for ${{
                                            preparedSubscription
                                                .subscription_plan.users_price
                                                ? preparedSubscription
                                                      .subscription_plan
                                                      .users_price / 100
                                                : 0
                                        }}/month. Paid monthly.
                                    </span>
                                </div>

                                <div
                                    v-if="
                                        selected ===
                                            'plugin_unlimited_monthly' &&
                                        pluginSubscription.plan?.uuid ===
                                            'plugin_unlimited_yearly' &&
                                        pluginSubscription.next_billing_date &&
                                        new Date(
                                            pluginSubscription.next_billing_date
                                        ) > new Date()
                                    "
                                    class="mt-5 text-sm text-gray-500"
                                >
                                    Your current yearly subscription will
                                    continue until
                                    <span class="font-medium text-gray-800">
                                        {{
                                            format(
                                                new Date(
                                                    pluginSubscription.next_billing_date
                                                ),
                                                'd MMMM, yyyy'
                                            )
                                        }}
                                    </span>
                                    and then will be switched to month-to-month
                                    payment. You will not be billed until then.
                                </div>

                                <div
                                    v-else-if="
                                        pluginSubscription.next_billing_date &&
                                        new Date(
                                            pluginSubscription.next_billing_date
                                        ) > new Date()
                                    "
                                    class="mt-5 text-sm text-gray-500"
                                >
                                    <div>
                                        {{
                                            preparedSubscription
                                                .subscription_plan.period === 1
                                                ? 'Month-to-month'
                                                : 'Annual'
                                        }}
                                        billing starts on
                                    </div>
                                    <div
                                        v-if="
                                            pluginSubscription.next_billing_date
                                        "
                                        class="font-medium text-gray-800"
                                    >
                                        {{
                                            format(
                                                new Date(
                                                    pluginSubscription.next_billing_date
                                                ),
                                                'd MMMM, yyyy'
                                            )
                                        }}
                                    </div>
                                </div>

                                <div v-else class="mt-5 text-sm text-gray-500">
                                    Your subscription will be activated
                                    immediately.
                                </div>
                            </div>
                            <div v-if="selected === 'cancel'">
                                <div class="mt-4 text-sm text-gray-500">
                                    <span
                                        v-if="
                                            pluginSubscription.next_billing_date &&
                                            new Date(
                                                pluginSubscription.next_billing_date
                                            ) > new Date()
                                        "
                                    >
                                        When cancelling your subscription,
                                        <span
                                            v-if="
                                                pluginSubscription.next_billing_date
                                            "
                                            class="font-medium text-gray-800"
                                            >your current subscription will
                                            continue until
                                            {{
                                                format(
                                                    new Date(
                                                        pluginSubscription.next_billing_date
                                                    ),
                                                    'd MMMM, yyyy'
                                                )
                                            }}.</span
                                        >
                                        After this date your
                                    </span>

                                    <span v-else>Your</span>

                                    subscription will be cancelled and you will
                                    be no longer able to use TOPOL Plugin. If
                                    you decide to continue with subscription,
                                    you can always subscribe again later.

                                    <p class="mt-2">
                                        Note that after your subscription is
                                        canceled, you will lose access to the
                                        images saved in our storage.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5">
                        <CheckboxInput
                            v-model:checked="confirmation"
                            size="md"
                            :disabled="
                                (pluginSubscription.plan?.uuid === selected &&
                                    !pluginSubscription.canceled_at) ||
                                (preparedSubscription &&
                                    hasPreparedPluginSubscriptionUnlimited)
                            "
                            name="confirm"
                            :label="
                                selected !== 'cancel'
                                    ? 'Confirm that you want to change your subscription'
                                    : 'Confirm that you want to CANCEL your subscription'
                            "
                        />
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between border-t border-gray-200 p-6">
                    <AtomicButton
                        color="white"
                        size="md"
                        @click="$emit('close')"
                    >
                        Cancel
                    </AtomicButton>

                    <AtomicButton
                        :disabled="
                            (pluginSubscription.plan?.uuid === selected &&
                                !pluginSubscription.canceled_at) ||
                            !confirmation ||
                            loading ||
                            (!preparedSubscription && selected !== 'cancel') ||
                            (preparedSubscription &&
                                hasPreparedPluginSubscriptionUnlimited)
                        "
                        size="md"
                        :color="selected === 'cancel' ? 'red' : 'yellow'"
                        @click="handleChange"
                    >
                        <span v-if="selected !== 'cancel'"
                            >Change subscription</span
                        >
                        <span v-else>Cancel subscription</span>
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>
<script lang="ts" setup>
import ISubscription from '@/types/Subscription/ISubscription';
import { format } from 'date-fns';
import { ref } from 'vue';
import { route } from 'ziggy-js';
import axios from 'axios';
import IApiResponse from '@/types/IApiResponse';
import PreparedSubscription from '@/types/Subscription/PreparedSubscription';
import { router } from '@inertiajs/vue3';
import { create } from '@/composables/useNotifications';
import { IPaymentMethod } from '@/types/Subscription/IPaymentMethod';
import SubscriptionPlan from '@/types/Subscription/SubscriptionPlan';
import { useGtm } from '@/composables/useGtm';
import { useStripeClient } from '@/composables/useStripe';

const emit = defineEmits(['close', 'cancel']);

const props = defineProps<{
    pluginSubscription: ISubscription;
    selectedCard?: IPaymentMethod;
    subscriptionPlans: SubscriptionPlan[];
    activeSubscriptionPlans: SubscriptionPlan[];
}>();

const { teamFeatureDisabled } = useFeatures();
const { pushEvent } = useGtm();
const isStripeEnabled = !teamFeatureDisabled('stripe');
const { finalizeSubscriptionSwitch } = useStripeClient('plugin');
const preparedSubscription = ref();
const hasPluginSubscriptionUnlimited =
    props.pluginSubscription.plan?.uuid.includes('unlimited');
const hasPreparedPluginSubscriptionUnlimited = computed(() =>
    preparedSubscription.value.plan?.uuid.includes('unlimited')
);

const loading = ref(false);
const confirmation = ref(false);
const selected = ref<number | string | undefined>(
    props.pluginSubscription?.plan?.uuid
);
const selectInput = ref();

const pluginPlans = () => {
    let plans: { title: string; value: number | string }[] = [];

    const planIds = props.activeSubscriptionPlans.map((plan) => plan.uuid);

    if (!planIds.includes(selected.value) && selected.value !== 'cancel') {
        plans.push({
            title: `${props.pluginSubscription.plan?.name} (current)`,
            value: props.pluginSubscription.plan?.uuid,
        });
    }

    plans = plans.concat(
        props.activeSubscriptionPlans.map((plan) => ({
            title: plan.name,
            value: plan.uuid,
            price: plan.price,
        }))
    );

    plans.sort((a, b) => (a.price || 0) - (b.price || 0));

    plans.push({
        title: 'Cancel Topol Plugin subscription',
        value: 'cancel',
    });

    return plans;
};

const handleChange = () => {
    if (
        selected.value !== 'cancel' &&
        selected.value !== props.pluginSubscription?.plan?.uuid
    ) {
        changeSubscription();
    }
    if (selected.value === 'cancel') {
        cancelSubscription();
    }
    if (
        selected.value === props.pluginSubscription?.plan?.uuid &&
        props.pluginSubscription?.canceled_at
    ) {
        resumeSubscription();
    }
};

const changeSubscription = () => {
    if (!props.selectedCard) {
        create({
            title: 'Subscription not changed',
            text: 'Please select a payment method before changing your subscription.',
            type: 'error',
        });
        return;
    }
    if(props.pluginSubscription === null){
        create({
            title: 'Subscription not changed',
            text: 'No active subscription found to change.',
            type: 'error',
        });
        return;
    }
    loading.value = true;

    const changeRoute = isStripeEnabled
        ? route('stripe.subscriptions.update', {
              subscription: props.pluginSubscription.id,
          })
        : route('subscriptions.switch', {
              subscription: props.pluginSubscription.id,
          });

    const method = isStripeEnabled ? 'put' : 'post';

    router[method](
        changeRoute,
        {
            subscription_plan_uuid: selected.value ?? null,
        },
        {
            // The switch is not done when the request comes back: Stripe still needs
            // the proration invoice paid, so the modal has to stay mounted long
            // enough to confirm the card and finalize.
            preserveState: true,
            onSuccess: async (response) => {
                if (!isStripeEnabled) {
                    create({
                        title: 'Subscription switched',
                        text: 'Your subscription has been successfully updated.',
                        type: 'success',
                    });
                    loading.value = false;
                    emit('close');
                    return;
                }

                try {
                    const status = await finalizeSubscriptionSwitch(
                        props.pluginSubscription.id,
                        selected.value ?? '',
                        response.props?.flash?.data,
                    );

                    create({
                        title: 'Subscription switched',
                        text:
                            status === 'pending'
                                ? 'Payment received. Your new plan will be active shortly.'
                                : 'Your subscription has been successfully updated.',
                        type: 'success',
                    });
                } catch (error) {
                    create({
                        title: 'Subscription not switched',
                        text:
                            error.message ??
                            'There was an error updating your subscription.',
                        type: 'error',
                    });
                    loading.value = false;
                    return;
                }

                loading.value = false;
                emit('close');
                router.reload();
            },
            onError: () => {
                loading.value = false;
                create({
                    title: 'Subscription not switched',
                    text: 'There was an error updating your subscription.',
                    type: 'error',
                });
            },
        }
    );
};

const cancelSubscription = () => {
    if (!props.pluginSubscription) {
        create({
            title: 'Subscription not changed',
            text: 'No active subscription found to change.',
            type: 'error',
        });
        return;
    }

    loading.value = true;

    const cancelRoute = isStripeEnabled
        ? route('stripe.subscriptions.cancel', {
              subscription: props.pluginSubscription.id,
          })
        : route('subscriptions.cancel', {
              subscription: props.pluginSubscription.id,
          });

    router.post(
        cancelRoute,
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: 'Subscription canceled',
                    text: 'Your Plugin subscription has been successfully canceled.',
                    type: 'success',
                });
                emit('cancel');
            },
            onError: () => {
                create({
                    title: 'Subscription not canceled',
                    text: 'There was an error canceling your Plugin subscription.',
                    type: 'error',
                });
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};

const resumeSubscription = () => {
    if (!props.pluginSubscription) {
        create({
            title: 'Subscription not changed',
            text: 'No active subscription found to change.',
            type: 'error',
        });
        return;
    }

    if (isStripeEnabled) {
        create({
            title: 'Subscription not resumed',
            text: 'Resuming subscriptions is not supported with Stripe integration. Please contact support.',
            type: 'error',
        });
        return;
    }

    loading.value = true;
    router.post(
        route('subscriptions.resume', {
            subscription: props.pluginSubscription.id,
        }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: 'Subscription extended',
                    text: 'Your Plugin subscription has been successfully extended.',
                    type: 'success',
                });

                pushEvent('topol_purchase_renewal', {
                    type: 'plugin',
                    amountPaid: preparedSubscription.value.total / 100,
                    planType:
                        preparedSubscription.value.subscription_plan.period ===
                        1
                            ? 'monthly'
                            : 'yearly',
                    currency: 'USD',
                });

                emit('close');
            },
            onError: () => {
                create({
                    title: 'Subscription not extended',
                    text: 'There was an error extending your Plugin subscription.',
                    type: 'error',
                });
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};

const getPreparedSubscription = async () => {
    if (
        selected.value !== 'cancel' &&
        selected.value !== props.pluginSubscription?.plan?.uuid
    ) {
        try {
            const res = await axios.get<IApiResponse<PreparedSubscription>>(
                route('subscriptions.prepare', {
                    subscription_plan_uuid: selected.value ?? null,
                })
            );
            preparedSubscription.value = res.data.data;
        } catch (error) {
            create({
                title: 'Subscription not prepared',
                text: 'There was an error preparing your subscription.',
                type: 'error',
            });
        }
    }
};
</script>
